import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { setSentryUser } from '@/lib/sentry';

/**
 * Google OAuth callback handler (Iter 142.8)
 * ==========================================
 * Mounted at /auth/google. Google redirects here with `?code=...&state=...`
 * after the user finishes the consent flow. We:
 *   1. Read code + state from URL.
 *   2. POST them to /api/auth/google/callback along with the EXACT redirect_uri
 *      we used at /start (window.location.origin + '/auth/google').
 *   3. On success: persist the session token, hydrate AuthContext, navigate
 *      the player to /app.
 *   4. On error: surface a friendly message + button back to /auth.
 *
 * This page is intentionally barebones — no app chrome, no sidebar, no auth
 * checks. Spend zero ms before completing the dance.
 */
export default function GoogleAuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUserDirect } = useAuth() || {};
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(true);
  // Guard against React StrictMode double-mount in dev. The OAuth code
  // is single-use; we MUST exchange it exactly once.
  const exchangedRef = useRef(false);

  useEffect(() => {
    if (exchangedRef.current) return;
    exchangedRef.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errParam = searchParams.get('error');

    if (errParam) {
      setBusy(false);
      setError(`Google declined the sign-in: ${errParam}`);
      return;
    }
    if (!code || !state) {
      setBusy(false);
      setError('Missing authorisation code from Google.');
      return;
    }

    const redirectUri = window.location.origin + '/auth/google';
    api
      .post('/api/auth/google/callback', { code, state, redirect_uri: redirectUri })
      .then((res) => {
        const { token, user } = res.data || {};
        if (token) localStorage.setItem('session_token', token);
        try { localStorage.removeItem('player_active_tab'); } catch {}
        if (user) {
          setSentryUser(user);
          if (typeof setUserDirect === 'function') {
            // If AuthContext exposes a setter, hydrate immediately so
            // the next route render skips the /me round-trip.
            setUserDirect(user);
          }
        }
        // Navigate to /app — main authenticated landing.
        navigate('/app', { replace: true });
      })
      .catch((err) => {
        const data = err.response?.data || {};
        // Backend uses a custom exception envelope: { ok, code, message, details }
        const friendly = data.message || data.detail || err.message || 'Google sign-in failed.';
        setError(friendly);
      })
      .finally(() => setBusy(false));
  }, [searchParams, navigate, setUserDirect]);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0B0E14',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
      data-testid="google-oauth-callback-page"
    >
      <div style={{ maxWidth: 420, textAlign: 'center' }}>
        {busy && (
          <>
            <div
              className="spinner"
              style={{ width: 36, height: 36, margin: '0 auto 18px' }}
              data-testid="google-callback-spinner"
            />
            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>
              Finishing Google sign-in…
            </h2>
            <p style={{ opacity: 0.6, fontSize: 14 }}>
              Hold tight, we're verifying with Google and creating your session.
            </p>
          </>
        )}
        {!busy && error && (
          <>
            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
              Sign-in didn't complete
            </h2>
            <p
              style={{ opacity: 0.7, fontSize: 14, marginBottom: 20 }}
              data-testid="google-callback-error-msg"
            >
              {error}
            </p>
            <button
              onClick={() => navigate('/auth')}
              style={{
                background: 'linear-gradient(135deg, #D4AF37, #F5D76E)',
                color: '#000',
                border: 'none',
                padding: '12px 28px',
                borderRadius: 12,
                fontWeight: 700,
                cursor: 'pointer',
              }}
              data-testid="google-callback-back-btn"
            >
              Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
